import { useState } from 'react';
import { TENANT_ROLES, type PortalUser, type TenantRole } from '@provalia/shared';
import { fmtDateTime } from '../../../shared/format';
import { Badge, Card, Confirm, Empty, ErrorBox, Loading, Modal, OneTimeSecret, useAsync, useToast } from '../../../shared/ui';
import { api, tenantPath } from '../../api';
import { roleLabel } from '../../labels';
import type { TenantTabProps } from '../TenantDetail';
import { UserModal } from './UserModal';
import type { InviteResult } from '../../types';

interface Sent {
  title: string;
  email: string;
  invite: InviteResult;
}

export function UsersTab({ slug, reload: reloadTenant }: TenantTabProps): JSX.Element {
  const toast = useToast();
  const users = useAsync(() => api.get<{ users: PortalUser[] }>(`${tenantPath(slug)}/users`).then((r) => r.users), [slug]);
  const [editing, setEditing] = useState<PortalUser | 'new' | null>(null);
  const [removing, setRemoving] = useState<PortalUser | null>(null);
  const [resetting, setResetting] = useState<PortalUser | null>(null);
  const [sent, setSent] = useState<Sent | null>(null);
  const [role, setRole] = useState<TenantRole | ''>('');

  const refresh = async (): Promise<void> => {
    await users.reload();
    void reloadTenant();
  };

  const list = (users.data ?? []).filter((u) => role === '' || u.role === role);

  return (
    <div className="stack lg">
      <Card
        bodyless
        title={
          <div>
            <h2>Usuarios del portal</h2>
            <p className="subtle">Los usuarios proveedor solo pueden ser de un proveedor ya habilitado en el portal del cliente.</p>
          </div>
        }
        actions={
          <>
            <select className="select sm-select" value={role} onChange={(e) => setRole(e.target.value as TenantRole | '')} aria-label="Filtrar por perfil">
              <option value="">Todos los perfiles</option>
              {TENANT_ROLES.map((r) => (
                <option key={r} value={r}>
                  {roleLabel(r)}
                </option>
              ))}
            </select>
            <button className="btn primary" onClick={() => setEditing('new')}>
              Nuevo usuario
            </button>
          </>
        }
      >
        {users.loading && !users.data ? (
          <Loading />
        ) : users.error ? (
          <div className="card-body">
            <ErrorBox error={users.error} onRetry={() => void users.reload()} />
          </div>
        ) : !users.data?.length ? (
          <Empty icon="＋">
            <p>Este cliente aún no tiene usuarios.</p>
            <p className="subtle">Empieza por su administrador: él podrá crear después a compras, recepción y proveedores desde su portal.</p>
          </Empty>
        ) : list.length === 0 ? (
          <Empty icon="⌕">Ningún usuario con el perfil «{roleLabel(role)}».</Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Correo</th>
                  <th>Perfil</th>
                  <th>Proveedor</th>
                  <th>Estado</th>
                  <th>Último acceso</th>
                  <th className="right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {list.map((u) => (
                  <tr key={u.id}>
                    <td>{u.name}</td>
                    <td className="break">{u.email}</td>
                    <td className="nowrap">{roleLabel(u.role)}</td>
                    <td className="mono">{u.cardCode ?? '—'}</td>
                    <td>
                      {!u.active ? <Badge>Inactivo</Badge> : u.passwordSet ? <Badge tone="ok">Activo</Badge> : <Badge tone="warn">Invitación pendiente</Badge>}
                    </td>
                    <td className="nowrap num">{u.lastLoginAt ? fmtDateTime(u.lastLoginAt) : <span className="subtle">Nunca</span>}</td>
                    <td className="right">
                      <div className="row end actions">
                        <button className="btn sm" onClick={() => setEditing(u)}>
                          Editar
                        </button>
                        <button className="btn sm" onClick={() => setResetting(u)}>
                          {u.passwordSet ? 'Cambiar contraseña' : 'Reenviar invitación'}
                        </button>
                        <button className="btn sm danger" onClick={() => setRemoving(u)} aria-label={`Borrar ${u.email}`}>
                          Borrar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing ? (
        <UserModal
          slug={slug}
          user={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async (saved) => {
            setEditing(null);
            if (saved.invite) setSent({ title: 'Usuario creado', email: saved.user.email, invite: saved.invite });
            await refresh();
          }}
        />
      ) : null}

      {resetting ? (
        <Confirm
          title={resetting.passwordSet ? 'Cambiar la contraseña' : 'Reenviar la invitación'}
          confirmLabel="Enviar el enlace"
          message={
            <p>
              {resetting.passwordSet
                ? <>Le mandamos a <strong>{resetting.email}</strong> un correo con un enlace para que elija una contraseña nueva. La actual sigue valiendo hasta que la cambie.</>
                : <>Le mandamos a <strong>{resetting.email}</strong> otra invitación para crear su contraseña. El enlace anterior deja de valer.</>}
            </p>
          }
          onClose={() => setResetting(null)}
          onConfirm={async () => {
            const res = await api.post<{ invite: InviteResult }>(`${tenantPath(slug)}/users/${encodeURIComponent(resetting.id)}/send-reset`);
            setSent({ title: resetting.passwordSet ? 'Enlace de contraseña' : 'Invitación', email: resetting.email, invite: res.invite });
          }}
        />
      ) : null}

      {removing ? (
        <Confirm
          title="Borrar usuario"
          danger
          confirmLabel="Borrar usuario"
          message={
            <p>
              <strong>{removing.name}</strong> ({removing.email}) dejará de poder entrar en el portal. Lo que hizo queda en la actividad.
              Si solo quieres impedirle el acceso por un tiempo, edítalo y desmárcalo como activo.
            </p>
          }
          onClose={() => setRemoving(null)}
          onConfirm={async () => {
            await api.del(`${tenantPath(slug)}/users/${encodeURIComponent(removing.id)}`);
            toast(`Usuario ${removing.email} borrado`, 'ok');
            await refresh();
          }}
        />
      ) : null}

      {sent ? (
        <Modal
          title={sent.title}
          onClose={() => setSent(null)}
          footer={
            <button className="btn primary" onClick={() => setSent(null)}>
              Hecho
            </button>
          }
        >
          {sent.invite.sent ? (
            <div className="callout ok">
              <div className="title">Correo enviado a {sent.email}</div>
              Lleva un enlace personal para crear su contraseña. Caduca en 7 días (1 hora si es un cambio de contraseña).
            </div>
          ) : (
            <div className="stack">
              <div className="callout warn">
                <div className="title">No se ha podido mandar el correo</div>
                {sent.invite.error}
              </div>
              {sent.invite.url ? (
                <OneTimeSecret
                  label="Enlace para crear la contraseña"
                  value={sent.invite.url}
                  note="Házselo llegar por otra vía. Es personal: quien lo tenga puede poner la contraseña de esta cuenta."
                />
              ) : null}
            </div>
          )}
        </Modal>
      ) : null}
    </div>
  );
}
